import { useMemo } from 'react';

import type {
  CustomFollowUpField,
  CustomFollowUpFieldValue,
  DetailedFollowUpTicket,
} from 'src/types/followUp';
import type { SurveyDetail } from 'src/types/survey';

import type { FormFieldKeys } from './constants';

interface useFollowUpCallFormReturnValue {
  initialValues?: FormFieldKeys & {
    customFields: Array<CustomFollowUpFieldValue & { required: boolean }>;
  };
  customFields?: Array<CustomFollowUpField>;
}

const useFollowUpCallForm = (
  ticket?: DetailedFollowUpTicket,
  survey?: SurveyDetail,
): useFollowUpCallFormReturnValue => {
  const disabled = ticket?.status === 'Closed';

  const customFields = useMemo(
    () =>
      survey?.specificSettings.followUp.customFields.filter(
        (field) =>
          field.isActive ||
          (disabled &&
            ticket?.followUpCall?.customFields.find(
              ({ customField: id }) => id === field.id,
            )?.value),
      ),
    [
      disabled,
      survey?.specificSettings.followUp.customFields,
      ticket?.followUpCall?.customFields,
    ],
  );

  const initialValues = useMemo(() => {
    if (!ticket) return undefined;
    if (ticket.followUpCall)
      return {
        ...ticket?.followUpCall,
        customFields:
          customFields?.map(({ id, isMandatory }) => ({
            customField: id,
            value:
              ticket?.followUpCall?.customFields.find(
                ({ customField }) => id === customField,
              )?.value ?? '',
            required: isMandatory,
          })) ?? [],
        callHappened: true,
        rootCauses: [...(ticket.followUpCall.rootCauses ?? [])],
        ticket: ticket.id,
      };
    return {
      callHappened: false,
      firstContactAt: new Date().toISOString().slice(0, 10),
      rootCauses: [],
      customFields:
        customFields?.map((customField) => ({
          customField: customField.id,
          value: '',
          required: customField.isMandatory,
        })) ?? [],
      npsGroup: ticket?.npsGroup,
      ticket: ticket.id,
    };
  }, [ticket?.followUpCall]);
  return { initialValues, customFields };
};

export default useFollowUpCallForm;
